import { useEffect, useMemo, useRef } from "react";
import { Layer, Marker, Source, useMap } from "@vis.gl/react-maplibre";
import "maplibre-gl/dist/maplibre-gl.css";

import MapContainer from "../fleetmap/MapContainer";
import vehicleCar from "../../assets/icon_truck.png";
import "./HistoryMap.css";

/**
 * HISTORY MAP
 *
 * Input coordinates: latLngArray / position = [latitude, longitude].
 * MapLibre coordinates: GeoJSON / Marker / camera = [longitude, latitude].
 *
 * Only the main map draws the full trip. The mini-map draws a short recent
 * trail, so repeated passes through intersections don't obscure the map.
 * The main map follows the vehicle's heading at 30 degrees of pitch, with a
 * plain rotating truck icon. The monochrome mini-map remains flat, north-up,
 * and uses a single rotating direction arrow.
 *
 * Tune visual styling here without changing CControlHistory.jsx.
 */
const DEFAULT_CENTER = [-33.86, 151.2]; // [lat, lng] for your MapContainer
const EMPTY_ARRAY = [];

const MAP_COLORS = {
  fullRoute: "#64748b", // quiet slate
  completed: "#2563eb", // blue playback progress
  start: "#16a34a",
  end: "#ef4444",
};

const LINE_LAYOUT = { "line-join": "round", "line-cap": "round" };

// Deliberately NO casing, glow, or dotted centerline on historical tracks.
const FULL_ROUTE_LAYER = {
  id: "history-full-route-layer",
  type: "line",
  layout: LINE_LAYOUT,
  paint: {
    "line-color": MAP_COLORS.fullRoute,
    "line-opacity": 0.48,
    "line-width": [
      "interpolate",
      ["linear"],
      ["zoom"],
      10,
      1.3,
      15,
      2.7,
      18,
      3.6,
    ],
  },
};

const COMPLETED_ROUTE_LAYER = {
  id: "history-completed-route-layer",
  type: "line",
  layout: LINE_LAYOUT,
  paint: {
    "line-color": MAP_COLORS.completed,
    "line-opacity": 0.95,
    "line-width": [
      "interpolate",
      ["linear"],
      ["zoom"],
      10,
      2.5,
      15,
      4,
      18,
      5.5,
    ],
  },
};

const ACTIVE_SEGMENT_LAYER = {
  id: "history-active-segment-layer",
  type: "line",
  layout: LINE_LAYOUT,
  paint: {
    "line-color": MAP_COLORS.completed,
    "line-opacity": 1,
    "line-width": [
      "interpolate",
      ["linear"],
      ["zoom"],
      10,
      2.5,
      15,
      4,
      18,
      5.5,
    ],
  },
};

const RECENT_TRAIL_LAYER = {
  id: "history-recent-trail-layer",
  type: "line",
  layout: LINE_LAYOUT,
  paint: {
    "line-color": MAP_COLORS.completed,
    "line-opacity": 0.88,
    "line-width": ["interpolate", ["linear"], ["zoom"], 10, 2, 15, 3.5, 18, 5],
  },
};

const degreesToRadians = (degrees) => (degrees * Math.PI) / 180;

function toLngLat(latLng) {
  if (!Array.isArray(latLng) || latLng.length < 2) return null;
  const lat = Number(latLng[0]);
  const lng = Number(latLng[1]);
  if (
    !Number.isFinite(lat) ||
    !Number.isFinite(lng) ||
    Math.abs(lat) > 90 ||
    Math.abs(lng) > 180 ||
    (lat === 0 && lng === 0) // usually a device's missing GPS fix
  ) {
    return null;
  }
  return [lng, lat];
}

function timeInMs(value) {
  if (value == null) return null;
  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) ? timestamp : null;
}

function distanceMeters(a, b) {
  const lat1 = degreesToRadians(a[1]);
  const lat2 = degreesToRadians(b[1]);
  const deltaLat = lat2 - lat1;
  const deltaLng = degreesToRadians(b[0] - a[0]);
  const sineLat = Math.sin(deltaLat / 2);
  const sineLng = Math.sin(deltaLng / 2);
  const h =
    sineLat * sineLat + Math.cos(lat1) * Math.cos(lat2) * sineLng * sineLng;
  return 2 * 6371000 * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * Decide whether two consecutive history records should have a visible line.
 * GPS jumps / large time gaps are represented by separate line segments.
 *
 * If both timestamps exist, the time gap and implied speed are meaningful.
 * Without timestamps, use the conservative maxUnverifiedJumpMeters threshold.
 */
function canConnect(from, to, rules) {
  if (!from?.coordinate || !to?.coordinate) return false;
  const meters = distanceMeters(from.coordinate, to.coordinate);

  if (from.time != null && to.time != null) {
    const elapsedMs = to.time - from.time;
    if (elapsedMs < 0 || elapsedMs > rules.maxGapMinutes * 60_000) {
      return false;
    }
    if (elapsedMs === 0) return meters < 10;

    // A modest 75m tolerance prevents ordinary GPS jitter from being flagged.
    const speedMeters = (rules.maxImpliedSpeedKmh / 3.6) * (elapsedMs / 1000);
    return meters <= speedMeters + 75;
  }

  return meters <= rules.maxUnverifiedJumpMeters;
}

function buildHistoryTrack(latLngArray, fleetData, rules) {
  const data = Array.isArray(fleetData) ? fleetData : EMPTY_ARRAY;
  const incoming = Array.isArray(latLngArray) ? latLngArray : EMPTY_ARRAY;
  const length = data.length || incoming.length;
  // Use latLngArray only when its indices align with playback's fleetData.
  const useIncoming = incoming.length === length && incoming.length > 0;

  const samples = Array.from({ length }, (_, index) => {
    const record = data[index];
    const rawCoords = record?.location?.coordinates;
    const latLng = useIncoming
      ? incoming[index]
      : Array.isArray(rawCoords)
        ? [rawCoords[1], rawCoords[0]]
        : null;

    return {
      index,
      coordinate: toLngLat(latLng),
      time: timeInMs(record?.timestamp),
    };
  });

  const runs = [];
  let currentRun = [];
  let previous = null;

  const saveRun = () => {
    if (currentRun.length >= 2) runs.push(currentRun);
    currentRun = [];
  };

  for (const sample of samples) {
    if (!sample.coordinate) {
      saveRun();
      previous = null;
      continue;
    }

    if (!previous || !canConnect(previous, sample, rules)) {
      saveRun();
      currentRun = [sample];
    } else if (distanceMeters(previous.coordinate, sample.coordinate) > 0.5) {
      // Adjacent duplicate points add visual weight but no route information.
      currentRun.push(sample);
    }

    previous = sample;
  }
  saveRun();

  const allCoordinates = samples
    .filter((sample) => sample.coordinate)
    .map((sample) => sample.coordinate);
  const drawnCoordinates = runs.flatMap((run) =>
    run.map((sample) => sample.coordinate),
  );

  return {
    samples,
    runs,
    rules,
    allCoordinates,
    fitCoordinates: drawnCoordinates.length ? drawnCoordinates : allCoordinates,
  };
}

// GeoJSON MultiLineString keeps discontinuous GPS stretches DISCONNECTED.
function makeLineGeoJson(lines) {
  const coordinates = lines.filter((line) => line.length >= 2);
  if (!coordinates.length) return null;
  return {
    type: "Feature",
    properties: {},
    geometry: { type: "MultiLineString", coordinates },
  };
}

function linesThroughIndex(runs, currentIndex, minIndex = 0, minTime = null) {
  return runs.map((run) =>
    run
      .filter(
        (sample) =>
          sample.index >= minIndex &&
          sample.index <= currentIndex &&
          (minTime == null || sample.time == null || sample.time >= minTime),
      )
      .map((sample) => sample.coordinate),
  );
}

function RouteLine({ sourceId, layer, data, color }) {
  if (!data) return null;

  return (
    <Source id={sourceId} type="geojson" data={data}>
      <Layer
        {...layer}
        paint={{
          ...layer.paint,
          "line-color": color ?? layer.paint["line-color"],
        }}
      />
    </Source>
  );
}

function EndpointMarker({ coordinate, type }) {
  if (!coordinate) return null;
  const label = type === "both" ? "S/E" : type === "start" ? "S" : "E";
  return (
    <Marker longitude={coordinate[0]} latitude={coordinate[1]} anchor="center">
      <div
        className={`history-map-endpoint history-map-endpoint-${type}`}
        title={type === "both" ? "Trip start and end" : `Trip ${type}`}
      >
        {label}
      </div>
    </Marker>
  );
}

function VehicleMarker({ position, bearing, status, variant }) {
  if (!position) return null;
  const [lng, lat] = position;
  const heading = Number.isFinite(bearing) ? bearing : 0;

  // The pitched, direction-following main map uses the plain truck image.
  // Keeping the marker viewport-aligned avoids distorting the image at 30deg pitch.
  if (variant !== "mini") {
    return (
      <Marker
        longitude={lng}
        latitude={lat}
        anchor="center"
        rotation={heading}
        rotationAlignment="map"
        pitchAlignment="viewport"
      >
        <img
          src={vehicleCar}
          alt="Vehicle"
          title="Vehicle position and heading"
          className="history-map-moving-truck-image"
          draggable={false}
        />
      </Marker>
    );
  }

  // The mini-map stays flat and north-up. The heading arrow rotates with the
  // vehicle, so it communicates travel direction relative to true north.
  const state = String(status || "").toLowerCase();
  const color =
    state === "offline"
      ? "#ef4444"
      : state === "paused"
        ? "#f59e0b"
        : "#2563eb";

  return (
    <Marker
      longitude={lng}
      latitude={lat}
      anchor="center"
      rotation={heading}
      rotationAlignment="map"
      pitchAlignment="viewport"
    >
      <div
        className="history-map-current-direction"
        style={{ "--history-direction-color": color }}
        title="Current direction of travel"
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          role="img"
          aria-label="Current direction of travel"
        >
          <path d="M12 2 21 21 12 17 3 21Z" fill="currentColor" />
        </svg>
      </div>
    </Marker>
  );
}

// Lives inside MapContainer: camera actions only affect this map instance.
function HistoryMapController({
  mapRef,
  position,
  bearing,
  isMini,
  mainPitch,
  followHeading,
  fitCoordinates,
  tracking,
  isPlaying,
  onTrackingChange,
  fitRouteOnLoad,
}) {
  const { current: map } = useMap();
  const fittedRef = useRef(false);
  const skipInitialFollowRef = useRef(fitRouteOnLoad);
  const lastPositionKeyRef = useRef(null);
  const lastHeadingRef = useRef(null);
  const previousTrackingRef = useRef(tracking);
  const lastFollowRef = useRef(0);
  const heading = Number.isFinite(bearing) ? ((bearing % 360) + 360) % 360 : 0;
  const targetPitch = isMini ? 0 : mainPitch;

  useEffect(() => {
    if (!map || !mapRef) return;
    mapRef.current = map;
    return () => {
      if (mapRef.current === map) mapRef.current = null;
    };
  }, [map, mapRef]);

  // Keep the overview completely flat and north-up, even after mouse/touch
  // interaction. The main map is pitched separately and can follow heading.
  useEffect(() => {
    if (!map) return;
    if (isMini) {
      map.jumpTo({ pitch: 0, bearing: 0 });
      const nativeMap = map.getMap();
      nativeMap.dragRotate.disable();
      nativeMap.keyboard.disableRotation();
      nativeMap.touchZoomRotate.disableRotation(); // keeps pinch zoom available
      nativeMap.touchPitch.disable();
    } else {
      map.jumpTo({ pitch: mainPitch });
    }
  }, [map, isMini, mainPitch]);

  useEffect(() => {
    if (!map || !onTrackingChange) return;
    const stopFollowing = (event) => {
      // Ignore programmatic fitBounds/easeTo; only actual gestures disable follow.
      if (event?.originalEvent) onTrackingChange(false);
    };
    map.on("dragstart", stopFollowing);
    map.on("zoomstart", stopFollowing);
    return () => {
      map.off("dragstart", stopFollowing);
      map.off("zoomstart", stopFollowing);
    };
  }, [map, onTrackingChange]);

  useEffect(() => {
    if (
      !map ||
      !fitRouteOnLoad ||
      fittedRef.current ||
      !fitCoordinates.length
    ) {
      return;
    }

    let west = Infinity;
    let south = Infinity;
    let east = -Infinity;
    let north = -Infinity;

    for (const [lng, lat] of fitCoordinates) {
      west = Math.min(west, lng);
      south = Math.min(south, lat);
      east = Math.max(east, lng);
      north = Math.max(north, lat);
    }

    fittedRef.current = true;
    if (Math.abs(east - west) < 1e-9 && Math.abs(north - south) < 1e-9) {
      map.jumpTo({
        center: [west, south],
        zoom: 16,
        pitch: targetPitch,
        bearing: isMini ? 0 : followHeading ? heading : map.getBearing(),
      });
    } else {
      map.fitBounds(
        [
          [west, south],
          [east, north],
        ],
        { padding: 45, maxZoom: 16, duration: 0 },
      );
      // fitBounds resets bearing to north; restore the heading and 30deg tilt.
      map.jumpTo({
        pitch: targetPitch,
        bearing: isMini ? 0 : followHeading ? heading : map.getBearing(),
      });
    }
  }, [
    map,
    fitCoordinates,
    fitRouteOnLoad,
    isMini,
    targetPitch,
    followHeading,
    heading,
  ]);

  useEffect(() => {
    const justEnabled = tracking && !previousTrackingRef.current;
    previousTrackingRef.current = tracking;

    if (!map || !position || !tracking) return;
    if (fitRouteOnLoad && !fittedRef.current) return;

    const positionKey = `${position[0]},${position[1]}`;
    if (skipInitialFollowRef.current) {
      skipInitialFollowRef.current = false;
      lastPositionKeyRef.current = positionKey;
      lastHeadingRef.current = heading;
      return;
    }

    const moved = lastPositionKeyRef.current !== positionKey;
    const headingChanged =
      !isMini &&
      followHeading &&
      (lastHeadingRef.current == null ||
        Math.abs(((heading - lastHeadingRef.current + 540) % 360) - 180) > 0.5);

    if (!isPlaying && !justEnabled && !moved && !headingChanged) {
      return;
    }

    const now = performance.now();
    if (isPlaying && now - lastFollowRef.current < 120) return;
    lastFollowRef.current = now;
    lastPositionKeyRef.current = positionKey;
    lastHeadingRef.current = heading;

    // When only the first heading becomes available after the initial fit,
    // rotate in place without prematurely zooming into the first GPS point.
    const recenter = !fitRouteOnLoad || moved || isPlaying || justEnabled;
    map.easeTo({
      ...(recenter ? { center: position } : {}),
      pitch: targetPitch,
      ...(isMini ? { bearing: 0 } : followHeading ? { bearing: heading } : {}),
      duration: isPlaying ? 120 : 0,
      essential: true,
    });
  }, [
    map,
    position,
    heading,
    isMini,
    mainPitch,
    followHeading,
    targetPitch,
    tracking,
    isPlaying,
    fitRouteOnLoad,
  ]);

  return null;
}

/**
 * Customizable history-only MapLibre component.
 *
 * currentIndex MUST match the chronological fleetData/latLngArray sample index.
 * Progress is drawn as two pieces: stable visited points (updated on index
 * changes) and one tiny active segment (updated as the vehicle interpolates).
 * This avoids sending the entire trip to MapLibre on every animation frame.
 */
export default function HistoryMap({
  variant = "main",
  mapRef,
  latLngArray = EMPTY_ARRAY,
  fleetData = EMPTY_ARRAY,
  currentIndex = 0,
  position,
  bearing = 0,
  status,
  isPlaying = false,
  tracking = true,
  onTrackingChange,
  zoom,
  fitRouteOnLoad = variant === "main",
  showEndpoints = variant === "main",
  showFullRoute = variant === "main",
  showProgress = variant === "main",
  showRecentTrail = variant === "mini",
  monochromeMiniMap = true,
  mainPitch = 30,
  followHeading = true,
  fullRouteColor = MAP_COLORS.fullRoute,
  completedColor = MAP_COLORS.completed,
  miniTrailPoints = 50,
  miniTrailMinutes = 10,
  maxGapMinutes = 12,
  maxImpliedSpeedKmh = 170,
  maxUnverifiedJumpMeters = 2000,
  className = "",
  onMapReady,
  onMapClick,
}) {
  const rules = useMemo(
    () => ({ maxGapMinutes, maxImpliedSpeedKmh, maxUnverifiedJumpMeters }),
    [maxGapMinutes, maxImpliedSpeedKmh, maxUnverifiedJumpMeters],
  );

  const track = useMemo(
    () => buildHistoryTrack(latLngArray, fleetData, rules),
    [latLngArray, fleetData, rules],
  );

  const safeIndex = Math.max(
    0,
    Math.min(track.samples.length - 1, Math.floor(Number(currentIndex) || 0)),
  );

  const fullGeoJson = useMemo(
    () =>
      makeLineGeoJson(
        track.runs.map((run) => run.map((sample) => sample.coordinate)),
      ),
    [track],
  );

  const completedGeoJson = useMemo(
    () => makeLineGeoJson(linesThroughIndex(track.runs, safeIndex)),
    [track, safeIndex],
  );

  const recentGeoJson = useMemo(() => {
    const currentTime = track.samples[safeIndex]?.time;
    const minTime =
      currentTime == null || miniTrailMinutes <= 0
        ? null
        : currentTime - miniTrailMinutes * 60_000;
    const minIndex = Math.max(0, safeIndex - Math.max(2, miniTrailPoints) + 1);
    return makeLineGeoJson(
      linesThroughIndex(track.runs, safeIndex, minIndex, minTime),
    );
  }, [track, safeIndex, miniTrailPoints, miniTrailMinutes]);

  const activeLngLat = useMemo(
    () =>
      toLngLat(position) ||
      track.samples[safeIndex]?.coordinate ||
      track.allCoordinates[0] ||
      null,
    [position, track, safeIndex],
  );

  const activeSegmentGeoJson = useMemo(() => {
    const from = track.samples[safeIndex];
    const to = track.samples[safeIndex + 1];
    if (!canConnect(from, to, rules) || !activeLngLat) return null;
    if (distanceMeters(from.coordinate, activeLngLat) < 0.5) return null;
    return makeLineGeoJson([[from.coordinate, activeLngLat]]);
  }, [track, safeIndex, rules, activeLngLat]);

  const start = track.allCoordinates[0];
  const end = track.allCoordinates[track.allCoordinates.length - 1];
  const endpointsOverlap = start && end && distanceMeters(start, end) < 20;
  const [startLng, startLat] = start || [];
  const initialCenter = start ? [startLat, startLng] : DEFAULT_CENTER;
  const isMini = variant === "mini";
  const monochromeClass =
    isMini && monochromeMiniMap ? "history-map--monochrome" : "";

  return (
    <div
      className={`${isMini ? "cch-mini-map" : "cch-main-map"} cch-maplibre-host ${monochromeClass} ${className}`.trim()}
    >
      <MapContainer
        center={initialCenter}
        zoom={zoom ?? (isMini ? 15 : 16)}
        onMapReady={onMapReady}
        onMapClick={onMapClick}
      >
        {showFullRoute && (
          <RouteLine
            sourceId="history-full-route-source"
            layer={FULL_ROUTE_LAYER}
            data={fullGeoJson}
            color={fullRouteColor}
          />
        )}

        {showProgress && (
          <RouteLine
            sourceId="history-completed-route-source"
            layer={COMPLETED_ROUTE_LAYER}
            data={completedGeoJson}
            color={completedColor}
          />
        )}

        {showRecentTrail && (
          <RouteLine
            sourceId="history-recent-trail-source"
            layer={RECENT_TRAIL_LAYER}
            data={recentGeoJson}
            color={completedColor}
          />
        )}

        {(showProgress || showRecentTrail) && (
          <RouteLine
            sourceId="history-active-segment-source"
            layer={ACTIVE_SEGMENT_LAYER}
            data={activeSegmentGeoJson}
            color={completedColor}
          />
        )}

        {showEndpoints &&
          start &&
          (endpointsOverlap ? (
            <EndpointMarker coordinate={start} type="both" />
          ) : (
            <>
              <EndpointMarker coordinate={start} type="start" />
              {end && <EndpointMarker coordinate={end} type="end" />}
            </>
          ))}

        <VehicleMarker
          position={activeLngLat}
          bearing={bearing}
          status={status}
          variant={variant}
        />

        <HistoryMapController
          mapRef={mapRef}
          position={activeLngLat}
          bearing={bearing}
          isMini={isMini}
          mainPitch={mainPitch}
          followHeading={followHeading}
          fitCoordinates={track.fitCoordinates}
          tracking={tracking}
          isPlaying={isPlaying}
          onTrackingChange={onTrackingChange}
          fitRouteOnLoad={fitRouteOnLoad}
        />
      </MapContainer>
    </div>
  );
}
